import React, { ReactNode } from 'react';
import { useAsyncFn } from 'react-use';

import {
  Box,
  createStyles,
  FormControl,
  makeStyles,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

type SelectOption = {
  label: string;
  value: string;
  [key: string]: string | undefined;
};

const useStyles = makeStyles(theme =>
  createStyles({
    formControl: {
      display: 'flex',
      flexDirection: 'row',
    },
    inputBox: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
    },
    inputLabel: {
      flex: '0 0 25%',
      height: '30px',
      lineHeight: '30px',
      display: 'inline-block',
      '& span.requiredStar': {
        color: theme.palette.primary.main,
        fontSize: theme.typography.caption.fontSize,
      },
    },
    inputElement: {
      flex: '1 1 auto',
    },
    inputElementSelect: {
      height: '30px',
    },
    inputElementSelectMulti: {
      flexWrap: 'wrap',
      padding: '6px',
    },
  }),
);

export const SelectInput = ({
  id,
  name,
  onChange,
  value,
  multiple,
  helperText,
  disabled,
  options,
  groupBy,
  error,
}: {
  id: string;
  label: string;
  name: string;
  value: string[] | string | null;
  options: SelectOption[] | ((inputValue?: string) => Promise<SelectOption[]>);
  groupBy?: (option: any) => string;
  multiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  onChange: (options: any) => void;
  helperText?: string | ReactNode;
  error?: boolean;
}) => {
  const classes = useStyles();

  const [show, setShow] = React.useState(
    typeof options !== 'function' || multiple,
  );
  const [inputValue, setInputValue] = React.useState('');
  const [availableOptions, setAvailableOptions] = React.useState<
    SelectOption[]
  >(typeof options === 'function' ? [] : options);

  const [optionsState, optionsFetch] = useAsyncFn(
    typeof options === 'function' ? options : async () => [],
    [options],
  );

  let onInputChange;

  if (typeof options === 'function')
    onInputChange = (e: any, v: string) => {
      setInputValue(v);
      if (v !== inputValue && e?.type === 'change') optionsFetch(v);
    };

  React.useEffect(() => {
    if (typeof options === 'function') {
      if (optionsState.loading) return;
      if (optionsState.value) {
        setAvailableOptions(optionsState.value);
        setShow(true);
      } else optionsFetch();
    } else {
      setAvailableOptions(options as SelectOption[]);
    }
  }, [value, options, optionsState, optionsFetch]);

  const getOptionLabel = (option: string | SelectOption): string =>
    typeof option === 'string'
      ? availableOptions.find(x => x?.value === option)?.label ?? option
      : option?.label ?? '';

  const getOptionSelected = (option: any, val: any): boolean =>
    option?.value === val;

  const handleChange = (_: any, v: any) =>
    onChange(multiple ? v.map((x: any) => x?.value ?? x) : v?.value ?? null);

  return (
    <FormControl fullWidth className={classes.formControl}>
      <Box className={classes.inputBox}>
        {show && (
          <Autocomplete
            id={id}
            multiple={multiple}
            options={availableOptions}
            groupBy={groupBy}
            openOnFocus
            onChange={handleChange}
            onBlur={_ => onChange(value)}
            onInputChange={onInputChange}
            value={value}
            getOptionSelected={getOptionSelected}
            getOptionLabel={getOptionLabel}
            disabled={disabled}
            renderInput={params => (
              <>
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  style={{ opacity: 0, height: 0, padding: 0, margin: 0 }}
                  value={value?.length ? 'ok' : ''}
                  required
                />
                <TextField
                  label="Security Groups*"
                  {...params}
                  className={classes.inputElement}
                  name={name}
                  error={error}
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    className: classes.inputElementSelectMulti,
                  }}
                  FormHelperTextProps={{
                    disabled: false,
                  }}
                  helperText={helperText}
                />
              </>
            )}
          />
        )}
      </Box>
    </FormControl>
  );
};
